import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  type DeliverySeries,
  useDeliverySeriesList,
} from "hooks/use-delivery-series-api";
import { cn } from "lib/utils";
import { ChevronDown, Megaphone, Plus, Send } from "lucide-react";
import { type FC, useEffect } from "react";

export type DeliveryTypeChoice = "single" | "existing" | "new";

export type DeliveryTypeSelection = {
  choice: DeliveryTypeChoice;
  /** existing 選択時のシリーズ ID. */
  selectedSeriesId: string | null;
  /** new 選択時のシリーズ名 (空文字 = 未入力). */
  newSeriesName: string;
};

export type DeliveryTypeSelectorProps = {
  value: DeliveryTypeSelection;
  onChange: (next: DeliveryTypeSelection) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * 配信新規作成 Step 1. 配信タイプ (単発 / 既存シリーズ / 新規シリーズ) を選ぶ.
 * 既存シリーズが 0 件の場合はデフォルトを 'new' に切り替える小さな配慮あり.
 * @param {DeliveryTypeSelectorProps} props 値・ハンドラ
 * @return {JSX.Element} Step 1 画面
 */
export const DeliveryTypeSelector: FC<DeliveryTypeSelectorProps> = ({
  value,
  onChange,
  onConfirm,
  onCancel,
}) => {
  const { items: seriesList, loading } = useDeliverySeriesList({
    status: "active",
    limit: 100,
  });

  // 既存シリーズが 0 件で、 ユーザーが existing をデフォルトで持っていた場合は new へ寄せる.
  useEffect(() => {
    if (loading) return;
    if (value.choice === "existing" && seriesList.length === 0) {
      onChange({ ...value, choice: "new" });
    }
  }, [loading, seriesList.length, value, onChange]);

  const canProceed =
    value.choice === "single" ||
    (value.choice === "existing" && Boolean(value.selectedSeriesId)) ||
    (value.choice === "new" && value.newSeriesName.trim().length > 0);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="text-xs text-base-content/60">Step 1 / 2</div>
        <h2 className="text-lg font-semibold">配信タイプを選択</h2>
        <p className="text-sm text-base-content/70">
          毎日繰り返し送る配信は「シリーズ」に束ねると、 リプライ管理 / 対応率
          KPI が シリーズ単位で見られるようになります。
        </p>
      </header>

      <div className="space-y-3">
        <OptionCard
          checked={value.choice === "single"}
          onSelect={() => onChange({ ...value, choice: "single" })}
          icon={null}
          title="単発配信 (今回限り)"
          description="今回限りの配信。 シリーズには紐付けない (series_id = null)"
        />
        <OptionCard
          checked={value.choice === "existing"}
          onSelect={() => onChange({ ...value, choice: "existing" })}
          icon={<Megaphone size={14} aria-hidden />}
          title="既存のシリーズに追加"
          description="過去のシリーズの一部として配信する。 直近の配信を雛型として自動コピーします"
        >
          {value.choice === "existing" && (
            <SeriesSelect
              loading={loading}
              series={seriesList}
              selectedId={value.selectedSeriesId}
              onSelect={id => onChange({ ...value, selectedSeriesId: id })}
            />
          )}
        </OptionCard>
        <OptionCard
          checked={value.choice === "new"}
          onSelect={() => onChange({ ...value, choice: "new" })}
          icon={<Plus size={14} aria-hidden />}
          title="新規シリーズを作成"
          description="この配信を起点に新しいシリーズを開始する"
        >
          {value.choice === "new" && (
            <label className="block mt-2">
              <span className="text-xs text-base-content/60">シリーズ名 *</span>
              <input
                type="text"
                className="input input-bordered input-sm w-full mt-1"
                value={value.newSeriesName}
                onChange={e =>
                  onChange({ ...value, newSeriesName: e.target.value })
                }
                placeholder="例) 2026/04/26 人材A 営業"
                maxLength={255}
              />
            </label>
          )}
        </OptionCard>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          キャンセル
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onConfirm}
          disabled={!canProceed}
        >
          <span className="inline-flex items-center gap-1">
            次へ
            <Send size={14} aria-hidden />
          </span>
        </Button>
      </div>
    </div>
  );
};

// ============================================================
// Sub-components
// ============================================================

type OptionCardProps = {
  checked: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  children?: React.ReactNode;
};

const OptionCard: FC<OptionCardProps> = ({
  checked,
  onSelect,
  icon,
  title,
  description,
  children,
}) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-lg border p-4 transition-colors block",
        checked
          ? "border-primary bg-primary/5"
          : "border-slate-200 bg-white hover:bg-slate-50",
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 inline-flex w-4 h-4 rounded-full border-2 shrink-0",
            checked ? "border-primary" : "border-slate-300",
          )}
          aria-hidden
        >
          {checked && (
            <span className="m-auto w-2 h-2 rounded-full bg-primary" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-base-content">
            {icon}
            {title}
          </div>
          <div className="text-xs text-base-content/60 mt-0.5">
            {description}
          </div>
          {children}
        </div>
      </div>
    </button>
  );
};

type SeriesSelectProps = {
  loading: boolean;
  series: DeliverySeries[];
  selectedId: string | null;
  onSelect: (id: string) => void;
};

const SeriesSelect: FC<SeriesSelectProps> = ({
  loading,
  series,
  selectedId,
  onSelect,
}) => {
  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-base-content/60">
        <Loading size="sm" />
        シリーズを読み込み中…
      </div>
    );
  }
  if (series.length === 0) {
    return (
      <div className="mt-2 text-xs text-base-content/60">
        active なシリーズがありません。 新規作成してください。
      </div>
    );
  }
  return (
    <div className="mt-2 relative">
      <select
        className="select select-bordered select-sm w-full"
        value={selectedId ?? ""}
        onChange={e => onSelect(e.target.value || "")}
      >
        <option value="" disabled>
          シリーズを選択...
        </option>
        {series.map(s => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.stats.deliveryCount} 配信)
          </option>
        ))}
      </select>
      <ChevronDown
        size={14}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 pointer-events-none"
        aria-hidden
      />
    </div>
  );
};
